import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "../assets/style.css";
import "../assets/careers.css";
import Git from "./Git";
import LatestNews from "./LatestNews";
import { useMessageModal } from "../admin/contexts/MessageModalContext";

<link
  href="https://fonts.googleapis.com/css?family=Roboto+Condensed:300,300i,400,400i,700,700i"
  rel="stylesheet"
></link>;

interface CareerJob {
  id: number;
  job_title: string;
  image: string;
  paragraph_1: string;
  paragraph_2: string;
  bullet_1: string;
  bullet_2: string;
  bullet_3: string;
  posting_date: string;
  last_date: string;
  status: "active" | "inactive";
}

interface ApplyFormState {
  job_id: string;
  full_name: string;
  email: string;
  phone: string;
  cover_letter: string;
  cvFile: File | null;
}

const API_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api";
const IMAGE_BASE_URL =
  import.meta.env.VITE_LYRAZONE_IMAGE_URL || "http://localhost:5000/uploads";

const Career: React.FC = () => {
  const [jobs, setJobs] = useState<CareerJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<CareerJob | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { showModal } = useMessageModal();

  const formatDateForInput = (dateString: string) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    if (isNaN(date.getTime())) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const [formData, setFormData] = useState<ApplyFormState>({
    job_id: "",
    full_name: "",
    email: "",
    phone: "",
    cover_letter: "",
    cvFile: null,
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/career-jobs/jobs`);
      const data = Array.isArray(response.data?.data) ? response.data.data : [];
      setJobs(data);
    } catch (error) {
      console.error("Error fetching career jobs:", error);
      setJobs([]);
    }
  };

  const getImageUrl = (image?: string) => {
    if (!image) return "";
    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }
    return `${IMAGE_BASE_URL}/uploads/career/${image}`;
  };

  const openApplyModal = (job: CareerJob) => {
    setSelectedJob(job);
    setFormData({
      job_id: String(job.id),
      full_name: "",
      email: "",
      phone: "",
      cover_letter: "",
      cvFile: null,
    });
    setApplyOpen(true);
  };

  const closeApplyModal = () => {
    setApplyOpen(false);
    setSelectedJob(null);
    setFormData({
      job_id: "",
      full_name: "",
      email: "",
      phone: "",
      cover_letter: "",
      cvFile: null,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // const handleCvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0] || null;
  //   setFormData((prev) => ({ ...prev, cvFile: file }));
  // };
  const handleCvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    setFormData((prev) => ({
      ...prev,
      cvFile: file,
    }));
  };

  const removeCvFile = () => {
    setFormData((prev) => ({
      ...prev,
      cvFile: null,
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const submitApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.cvFile) {
      showModal({
        type: "error",
        title: "No attachment",
        message: "Please attach your CV before submitting.",
        confirmText: "OK",
      });
      return;
    }
    try {
      setSubmitting(true);

      const form = new FormData();
      form.append("job_id", formData.job_id);
      form.append("full_name", formData.full_name);
      form.append("email", formData.email);
      form.append("phone", formData.phone);
      form.append("cover_letter", formData.cover_letter);

      if (formData.cvFile) {
        form.append("cvFile", formData.cvFile);
      }

      const response = await axios.post(
        `${API_URL}/admin/career-applications`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (response.data?.success) {
        showModal({
          type: "success",
          title: "success",
          message: "Application submitted successfully.",
          confirmText: "OK",
          onConfirm: () => {
            closeApplyModal();
          },
        });

        // closeApplyModal();
      } else {
        showModal({
          type: "error",
          title: "Failed",
          message: "Failed to submit application.",
          confirmText: "OK",
        });
      }
    } catch (error: any) {
      console.error("Submit application error:", error);
      showModal({
        type: "error",
        title: "Failed",
        message:
          error?.response?.data?.message || "Failed to submit application.",
        confirmText: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div id="home-p" className="home-p pages-head2 text-center">
        <div className="container">
          {/* <h1 className="wow fadeInUp" data-wow-delay="0.1s">
            Lyra Zone is fully equipped with ICT tools
          </h1>
          <p>Our Services</p> */}
        </div>
      </div>
      {/* Career-P1 Section */}
      <section id="career-p1" className="career-p1">
        <div className="container">
          <div className="row">
            {jobs.length > 0 ? (
              jobs.map((job) => {
                const isJobExpired =
                  !!job.last_date &&
                  new Date(job.last_date).setHours(23, 59, 59, 999) <
                    Date.now();

                return (
                  <div className="col-md-12" key={job.id}>
                    <div className="career-job-card">
                      <div className="row">
                        <div className="col-md-4">
                          {job.image && (
                            <div className="career-job-image-wrap">
                              <img
                                src={getImageUrl(job.image)}
                                alt={job.job_title}
                                className="img-fluid"
                              />
                            </div>
                          )}
                        </div>

                        <div className="col-md-8">
                          <div className="career-job-content">
                            <h3>{job.job_title}</h3>

                            {job.posting_date && (
                              <p>
                                <strong>Job Posting Date:</strong>{" "}
                                {formatDateForInput(job.posting_date)}
                              </p>
                            )}

                            {job.last_date && (
                              <p>
                                <strong>Last Date:</strong>{" "}
                                {formatDateForInput(job.last_date)}
                              </p>
                            )}

                            {job.paragraph_1 && <p>{job.paragraph_1}</p>}
                            {job.paragraph_2 && <p>{job.paragraph_2}</p>}

                            <ul className="career-job-bullets">
                              {job.bullet_1 && <li>{job.bullet_1}</li>}
                              {job.bullet_2 && <li>{job.bullet_2}</li>}
                              {job.bullet_3 && <li>{job.bullet_3}</li>}
                            </ul>

                            <div className="career-job-actions">
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => openApplyModal(job)}
                                disabled={isJobExpired}
                              >
                                {isJobExpired ? "Expired" : "Apply Now"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-md-12 text-center">
                <p>No jobs available right now.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {applyOpen && selectedJob && (
        <div className="career-apply-modal-overlay">
          <div className="career-apply-modal">
            <button
              type="button"
              className="career-apply-close"
              onClick={closeApplyModal}
            >
              ×
            </button>

            <h3>Apply for</h3>
            <h2>{selectedJob.job_title}</h2>

            <form onSubmit={submitApplication}>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label>Cover Letter</label>
                <textarea
                  name="cover_letter"
                  value={formData.cover_letter}
                  onChange={handleChange}
                  className="form-control"
                  required
                  rows={5}
                />
              </div>

              <div className="form-group">
                {/* <label>Upload CV</label> */}

                <div className="career-cv-upload">
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="cvFile"
                    accept=".pdf,.doc,.docx"
                    onChange={handleCvChange}
                    className="career-cv-hidden-input"
                  />

                  <label htmlFor="cvFile" className="career-cv-attach-btn">
                    <i className="fa fa-paperclip" aria-hidden="true"></i>
                    <span>Attach CV</span>
                  </label>

                  {formData.cvFile && (
                    <div className="career-cv-file-box">
                      <button
                        type="button"
                        className="career-cv-remove-btn"
                        onClick={removeCvFile}
                        aria-label="Remove attached file"
                      >
                        ×
                      </button>

                      <div className="career-cv-file-content">
                        <i className="fa fa-file-text-o" aria-hidden="true"></i>
                        <span className="career-cv-file-name">
                          {formData.cvFile.name}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="career-apply-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Career-P2 Section */}
      <div className="overlay-career-p2"></div>
      <section id="thought" className="thought-bg career-p2-bg">
        <div className="container">
          <div id="row" className="row title-bar-career-p2">
            <div className="col-md-4 ">
              <h1>100%</h1>
            </div>
            <div className="col-md-8 ">
              <p
                className="wow fadeInUp "
                data-wow-delay="0.4s"
                style={{ textAlign: "justify" }}
              >
                LyraZone will deliver value to all the stakeholders and will
                attain excellence and leadership through such delivery of value.
                We will strive to support the stakeholders in all activities
                related to us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Career-P3 Section */}
      <div className="overlay-career-p3"></div>
      <section id="career-p3">
        <div className="container-fluid">
          <div className="row career-p3-title">
            <div className="col-md-12">
              <h3 className="text-center">Our Business Process</h3>
            </div>
          </div>
          <div className="row">
            {[
              { icon: "fa-superpowers", title: "Plan" },
              { icon: "fa-snowflake-o", title: "Seamless" },
              { icon: "fa-send-o", title: "Care" },
              { icon: "fa-sun-o", title: "Transit" },
              { icon: "fa-crosshairs", title: "Vacation" },
              { icon: "fa-gift", title: "Enjoy" },
              { icon: "fa-plane", title: "Celebrate" },
              { icon: "fa-life-buoy", title: "Contrary" },
            ].map((item, index) => (
              <div className="col-md-3 col-sm-6" key={index}>
                <div className="career-p3-cont text-center">
                  <i className={`fa ${item.icon}`}></i>
                  <h5>{item.title}</h5>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Git />
      <LatestNews />
    </>
  );
};

export default Career;
